import {SectionDiff, SectionEntry, MapSectionKey} from "../../utils/branchDiff";
import {RemoteConstantsModel} from "../../models/remoteConstants.model";
import {useIntegrator} from "../../integrator.context";
import {SetMapEntryCommand} from "../../commands/setMapEntry.command";
import {RemoveMapEntryCommand} from "../../commands/removeMapEntry.command";
import {extraImagesForPack, mapSectionRoot, readPath} from "./sectionActions";
import ActionButton from "./actionButton";

// Renders a key→value MAP section (localization / sounds / images / imagePacks).
// Three groups: entries only in Other (migrate in), only in Current (remove to
// match Other), and present-but-different (replace Current's value with Other's).

function formatValue(v: unknown): string {
    if (typeof v === 'string') return v === '' ? '""' : v;
    return JSON.stringify(v);
}

function MapSectionView(props: { section: SectionDiff; targetData: RemoteConstantsModel }) {
    const {state, executeCommand} = useIntegrator();
    const s = props.section;
    const sectionKey = s.sectionKey as MapSectionKey;
    const targetRoot = mapSectionRoot(props.targetData, sectionKey);
    const currentRoot = mapSectionRoot(state, sectionKey);

    const pull = (path: string[]) => {
        const value = readPath(targetRoot, path);
        const extra = extraImagesForPack(sectionKey, value, props.targetData, state);
        executeCommand(new SetMapEntryCommand(sectionKey, path, value, extra));
    };
    const remove = (path: string[]) => executeCommand(new RemoveMapEntryCommand(sectionKey, path));

    return (
        <div className={'layout vertical gap'} style={{gap: 8, marginTop: 8}}>
            {s.onlyInTarget.length > 0 && (
                <div className={'layout vertical'}>
                    <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 13}}>Only in Other ({s.onlyInTarget.length})</p>
                    {s.onlyInTarget.map((e: SectionEntry) => (
                        <div key={e.identity} className={'layout horizontal'} style={{paddingLeft: 10, alignItems: 'center', gap: 8}}>
                            <p style={{flexGrow: 1}}>+ {e.name}</p>
                            <ActionButton variant={'pull'} label={'Pull'}
                                          title={'Migrate this entry from Other into Current'}
                                          onClick={() => pull(e.path ?? [e.identity])}/>
                        </div>
                    ))}
                </div>
            )}
            {s.onlyInCurrent.length > 0 && (
                <div className={'layout vertical'}>
                    <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 13}}>Only in Current ({s.onlyInCurrent.length})</p>
                    {s.onlyInCurrent.map((e: SectionEntry) => (
                        <div key={e.identity} className={'layout horizontal'} style={{paddingLeft: 10, alignItems: 'center', gap: 8}}>
                            <p style={{flexGrow: 1}}>- {e.name}</p>
                            <ActionButton variant={'remove'} label={'Remove'}
                                          title={'Remove from Current to match Other'}
                                          onClick={() => remove(e.path ?? [e.identity])}/>
                        </div>
                    ))}
                </div>
            )}
            {s.differs.length > 0 && (
                <div className={'layout vertical'}>
                    <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 13}}>Differs ({s.differs.length})</p>
                    {s.differs.map(r => {
                        const path = r.path ?? [r.identity];
                        const currentVal = readPath(currentRoot, path);
                        const targetVal = readPath(targetRoot, path);
                        return (
                            <div key={r.identity} className={'layout horizontal gap'}
                                 style={{paddingLeft: 10, gap: 8, alignItems: 'flex-start'}}>
                                <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 12, minWidth: 160}}>{r.name}</p>
                                <p style={{color: '#ff6666', flexShrink: 1, overflowWrap: 'anywhere'}}>{formatValue(currentVal)}</p>
                                <span className={'fa-solid fa-arrow-right'} style={{color: 'rgba(255,255,255,0.66)', fontSize: 11, alignSelf: 'center'}}/>
                                <p style={{color: '#66cc66', flexGrow: 1, overflowWrap: 'anywhere'}}>{formatValue(targetVal)}</p>
                                <ActionButton variant={'pull'} label={'Pull'}
                                              title={"Replace Current's value with Other's"}
                                              onClick={() => pull(path)}/>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default MapSectionView;
